import React, { useEffect, useRef, useCallback } from 'react';

/**
 * Cloudflare Turnstile widget.
 *
 * Renders the invisible/managed challenge and hands the solved token up to the
 * parent via onVerify(token). On expiry/error it calls onVerify('') so the
 * parent can disable submit until it re-solves.
 *
 * Site key comes from REACT_APP_TURNSTILE_SITE_KEY. If that env var is not set
 * the component renders nothing and immediately reports an empty token — the
 * backend fail-open contract (app/core/turnstile.py) means login/register still
 * work while the key is being wired up.
 */
const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

let scriptPromise = null;
function loadScript() {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return scriptPromise;
}

export default function Turnstile({ onVerify, resetKey = 0 }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const errorRearmsRef = useRef(0);

  const stableOnVerify = useCallback((t) => { onVerify?.(t); }, [onVerify]);

  useEffect(() => {
    // No key configured yet -> nothing to render; report empty token so the
    // parent doesn't wait on us (backend fail-open still lets the request through).
    if (!SITE_KEY) {
      stableOnVerify('');
      return;
    }

    let cancelled = false;

    // A transient widget error leaves the page tokenless forever. Re-arm a
    // couple of times, then stop — an unbounded retry against a genuinely
    // blocked Cloudflare would spin.
    const rearmAfterError = () => {
      if (errorRearmsRef.current >= 2) return;
      errorRearmsRef.current += 1;
      try {
        if (widgetIdRef.current && window.turnstile) window.turnstile.reset(widgetIdRef.current);
      } catch (_) { /* widget gone; next mount re-renders it */ }
    };

    loadScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: SITE_KEY,
          callback: (token) => { errorRearmsRef.current = 0; stableOnVerify(token); },
          // Tokens are single-use AND expire after ~5 minutes. 'auto' makes
          // Cloudflare mint a fresh one in place. Without it, a form left open
          // longer than that submits an EMPTY token, the backend (fail-closed
          // once TURNSTILE_SECRET_KEY is set) answers 403 "Human verification
          // failed", and nothing recovers it but a full reload. This is the
          // single most common cause of that error in production.
          'refresh-expired': 'auto',
          'refresh-timeout': 'auto',
          'expired-callback': () => stableOnVerify(''),
          'error-callback': () => { stableOnVerify(''); rearmAfterError(); },
          theme: 'light',
        });
      })
      .catch(() => {
        // Cloudflare script blocked/failed to load — don't hard-block the user;
        // backend soft-passes a transient failure.
        stableOnVerify('');
      });

    return () => {
      cancelled = true;
      try {
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.remove(widgetIdRef.current);
        }
      } catch (_) { /* widget already gone */ }
    };
  }, [stableOnVerify]);

  // Tokens are single-use. After a failed submit the page would otherwise
  // re-send the same consumed token forever ("timeout-or-duplicate" from
  // Cloudflare, surfaced as "Human verification failed" on every retry until a
  // full reload). Bumping resetKey re-arms the widget for a fresh token.
  useEffect(() => {
    if (!resetKey) return;
    try {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
      }
    } catch (_) { /* widget gone; next mount re-renders it */ }
  }, [resetKey]);

  if (!SITE_KEY) return null;
  return <div ref={containerRef} style={{ margin: '4px 0 16px' }} />;
}
